import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '@udbodhanam/shared';
import apiClient from '../../services/apiClient';
import LanguageSelector from '../../components/UI/LanguageSelector';
import ThemeToggle from '../../components/UI/ThemeToggle';

type ValidationReason = 'NOT_FOUND' | 'USED' | 'EXPIRED';

interface GroupStudent {
  id: string;
  fullName: string;
}

interface ValidationResult {
  valid: boolean;
  reason?: ValidationReason;
  students?: GroupStudent[];
}

interface GroupResult {
  grantedCount: number;
  failedCount: number;
  remainingCount: number;
}

type View = 'loading' | 'form' | 'invalid' | 'done';

export default function GuardianConsentGroup() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [view, setView] = useState<View>('loading');
  const [students, setStudents] = useState<GroupStudent[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState<ValidationReason | undefined>();
  const [result, setResult] = useState<GroupResult | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function validate() {
      try {
        const res = await apiClient.get<{ data: ValidationResult }>(
          '/auth/guardian-consent/group/validate',
          { params: { token } },
        );
        if (!active) return;
        const data = res.data.data;
        if (data.valid) {
          setStudents(data.students ?? []);
          setView('form');
        } else {
          setReason(data.reason);
          setView('invalid');
        }
      } catch {
        if (!active) return;
        setReason('NOT_FOUND');
        setView('invalid');
      }
    }
    void validate();
    return () => {
      active = false;
    };
  }, [token]);

  const allSelected = students.length > 0 && selectedIds.length === students.length;

  function toggleStudent(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit() {
    setError('');
    setSubmitting(true);
    try {
      const res = await apiClient.post<{ data: GroupResult }>('/auth/guardian-consent/group', {
        token,
        tokenIds: selectedIds,
        confirmed,
      });
      setResult(res.data.data);
      setView('done');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message ?? t('guardianConsent.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface px-4 py-10 relative">
      <div className="absolute top-5 right-5 flex items-center gap-2">
        <ThemeToggle />
        <LanguageSelector />
      </div>
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <p className="text-sm font-semibold text-primary-600">{APP_NAME}</p>
        </div>

        {view === 'loading' && (
          <div className="bg-surface border border-border rounded-xl p-8 text-center">
            <div className="mx-auto h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
            <p className="mt-4 text-sm text-earth-600">{t('guardianConsent.validating')}</p>
          </div>
        )}

        {view === 'invalid' && (
          <div className="bg-surface border border-border rounded-xl p-[22px] text-center space-y-3">
            <h1 className="text-[22px] leading-7 font-semibold text-earth-800">
              {t('guardianConsent.invalidTitle')}
            </h1>
            <p className="text-sm text-earth-600">
              {reason === 'USED'
                ? t('guardianConsent.alreadyUsed')
                : t('guardianConsent.expired')}
            </p>
          </div>
        )}

        {view === 'form' && (
          <div className="bg-surface border border-border rounded-xl shadow-card p-[22px] space-y-5">
            <div>
              <h1 className="text-[22px] leading-7 font-semibold text-earth-800 tracking-[-0.01em]">
                {t('guardianConsent.title')}
              </h1>
              <p className="mt-2 text-sm text-earth-600">{t('guardianConsent.group.intro')}</p>
            </div>

            <div className="bg-viveka-50 border border-viveka-200 rounded-lg p-3 text-viveka-800 text-sm flex items-start gap-2">
              <span className="shrink-0 mt-0.5">🔒</span>
              <span>{t('guardianConsent.dpdpNotice')}</span>
            </div>

            <div className="border border-border rounded-lg p-3 space-y-2 text-sm text-earth-700">
              <p className="font-semibold text-earth-800">{t('guardianConsent.dataCollectedTitle')}</p>
              <p>{t('guardianConsent.dataCollectedBody')}</p>
            </div>

            <div className="border border-border rounded-lg divide-y divide-border">
              <label className="flex items-center gap-3 px-3 py-2.5 bg-earth-50 rounded-t-lg text-sm font-semibold text-earth-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary-500"
                  checked={allSelected}
                  onChange={() => setSelectedIds(allSelected ? [] : students.map((s) => s.id))}
                />
                {t('guardianConsent.group.selectAll', { total: students.length })}
              </label>
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {students.map((student) => (
                  <label
                    key={student.id}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-earth-700 cursor-pointer hover:bg-earth-50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary-500"
                      checked={selectedIds.includes(student.id)}
                      onChange={() => toggleStudent(student.id)}
                    />
                    {student.fullName}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm text-earth-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary-500"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              {t('guardianConsent.group.confirm')}
            </label>

            {error !== '' && <p className="text-sm text-error">{error}</p>}

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => void submit()}
                disabled={submitting || !confirmed || selectedIds.length === 0}
                className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? t('guardianConsent.processing')
                  : t('guardianConsent.group.grantButton', { selected: selectedIds.length })}
              </button>
              <p className="text-xs text-earth-500">{t('guardianConsent.group.declineHint')}</p>
            </div>
          </div>
        )}

        {view === 'done' && result && (
          <div className="bg-surface border border-border rounded-xl p-[22px] text-center space-y-3">
            <div className="mx-auto w-11 h-11 rounded-full bg-growth-50 flex items-center justify-center text-growth-600 text-2xl">
              ✓
            </div>
            <h1 className="text-[22px] leading-7 font-semibold text-earth-800">
              {t('guardianConsent.grantedTitle')}
            </h1>
            <p className="text-sm text-earth-600">
              {t('guardianConsent.group.grantedMessage', { granted: result.grantedCount })}
            </p>
            {result.failedCount > 0 && (
              <p className="text-sm text-error">
                {t('guardianConsent.group.failedMessage', { failed: result.failedCount })}
              </p>
            )}
            {result.remainingCount > 0 && (
              <p className="text-sm text-earth-600">
                {t('guardianConsent.group.remainingMessage', { remaining: result.remainingCount })}
              </p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
